import { useEffect, useSyncExternalStore } from "react";
import type {
  CmmdRuntimeInventoryRuntime,
  CmmdRuntimeInventorySnapshot,
  CmmdRuntimeInventoryThreadSummary,
  ReplaceCmmdRuntimeInventoryParams,
} from "@cmmd-forge/contracts";

import { cmmdApiClient } from "../environments/cmmdApi";
import type { GetCmmdRuntimeInventoryParams } from "../environments/cmmdApi/runtimeInventoryQuery";

type Listener = () => void;

export interface CmmdRuntimeInventoryResourceSnapshot {
  readonly inventory: CmmdRuntimeInventorySnapshot | null;
  readonly isLoading: boolean;
  readonly error: string | null;
}

export function findCmmdRuntimeInventoryThread(
  inventory: CmmdRuntimeInventorySnapshot | null,
  runtimeId: string,
  projectId: string,
  threadId: string,
): CmmdRuntimeInventoryThreadSummary | undefined {
  const runtime = inventory?.runtimes.find((entry) => entry.runtime.id === runtimeId);
  const project = runtime?.projects.find((entry) => String(entry.projectId) === projectId);
  return project?.threads.find((entry) => entry.threadId === threadId);
}

const INITIAL_SNAPSHOT: CmmdRuntimeInventoryResourceSnapshot = {
  inventory: null,
  isLoading: false,
  error: null,
};

function scopeKey(params: GetCmmdRuntimeInventoryParams): string {
  return "organizationId" in params && params.organizationId !== undefined
    ? `org:${params.organizationId}`
    : "all";
}

export interface CreateCmmdRuntimeInventoryResourceOptions {
  /**
   * Called on each passive refresh to determine the scope. When a current
   * workspace is known, return `{ organizationId }` to narrow the server
   * response. When absent, the resource defaults to `{ allWorkspaces: true }`.
   *
   * Correctness callers (thread lifecycle, archive) always pass an explicit
   * override to `refresh()` so they are unaffected by this callback.
   */
  readonly getRefreshScope?: () => GetCmmdRuntimeInventoryParams;
}

export function createCmmdRuntimeInventoryResource(
  options: CreateCmmdRuntimeInventoryResourceOptions = {},
) {
  let snapshot = INITIAL_SNAPSHOT;
  let initialized = false;
  /**
   * One in-flight request per scope key. Deduplication is per-scope so a
   * passive `{ organizationId }` request cannot satisfy an `allWorkspaces`
   * correctness override, and vice versa.
   */
  const activeRequests = new Map<string, Promise<CmmdRuntimeInventorySnapshot>>();
  /**
   * Increments with each new request. The emit at settlement is skipped when a
   * newer request has already replaced the snapshot, preventing an older
   * response from overwriting newer data.
   */
  let snapshotGeneration = 0;
  const listeners = new Set<Listener>();

  function emit(next: CmmdRuntimeInventoryResourceSnapshot): void {
    snapshot = next;
    for (const listener of listeners) listener();
  }

  function refresh(
    override?: GetCmmdRuntimeInventoryParams,
  ): Promise<CmmdRuntimeInventorySnapshot> {
    // An explicit override (used by correctness callers) takes precedence over
    // the factory-level scope. When neither is provided, fall back to all
    // workspaces so the resource is safe to use without a workspace store.
    const params: GetCmmdRuntimeInventoryParams = override ??
      options.getRefreshScope?.() ?? { allWorkspaces: true };

    // Deduplicate within the same scope. Direct thread, sidebar, and new-thread
    // mounts may overlap under Strict Mode, but one passive read per scope must
    // produce one CMMD request. A request for a different scope always starts a
    // new fetch so correctness callers get a full-inventory response even when a
    // scoped passive request is in-flight.
    const key = scopeKey(params);
    const existing = activeRequests.get(key);
    if (existing !== undefined) return existing;

    const myGeneration = ++snapshotGeneration;

    const request = (async () => {
      emit({ ...snapshot, isLoading: true, error: null });
      try {
        const inventory = await cmmdApiClient.getRuntimeInventory(params);
        // Only emit when no newer request has already settled a snapshot.
        if (snapshotGeneration === myGeneration) {
          emit({ inventory, isLoading: false, error: null });
        }
        return inventory;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Could not load CMMD runtime inventory.";
        if (snapshotGeneration === myGeneration) {
          emit({ ...snapshot, isLoading: false, error: message });
        }
        throw error;
      }
    })();
    activeRequests.set(key, request);
    const clearRequest = () => {
      if (activeRequests.get(key) === request) activeRequests.delete(key);
    };
    void request.then(clearRequest, clearRequest);
    return request;
  }

  return {
    subscribe(listener: Listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    read(): CmmdRuntimeInventoryResourceSnapshot {
      return snapshot;
    },
    initialize(): void {
      if (initialized) return;
      initialized = true;
      void refresh().catch(() => undefined);
    },
    refresh,
    async replace(
      runtimeId: string,
      params: ReplaceCmmdRuntimeInventoryParams,
    ): Promise<CmmdRuntimeInventoryRuntime> {
      try {
        const runtime = await cmmdApiClient.replaceRuntimeInventory(runtimeId, params);
        const existing = snapshot.inventory?.runtimes ?? [];
        const runtimes = existing.some((entry) => entry.runtime.id === runtime.runtime.id)
          ? existing.map((entry) => (entry.runtime.id === runtime.runtime.id ? runtime : entry))
          : [...existing, runtime];
        emit({ inventory: { runtimes }, isLoading: false, error: null });
        return runtime;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Could not sync CMMD runtime inventory.";
        emit({ ...snapshot, isLoading: false, error: message });
        throw error;
      }
    },
  };
}

/**
 * The scope getter for the singleton. Injected at boot by
 * `configureRuntimeInventoryRefreshScope` so this module does not carry a
 * static import of `currentForgeWorkspaceStore` (which would pull in
 * `cmmdForgeRuntimeListResource` and the WebSocket supervisor, breaking every
 * unit test that imports the inventory resource).
 */
let _refreshScopeGetter: (() => GetCmmdRuntimeInventoryParams) | undefined;

/**
 * Called once at application boot (from `currentForgeWorkspaceStore`) to wire
 * `getCurrentForgeWorkspaceId` into the singleton without a static import.
 */
export function configureRuntimeInventoryRefreshScope(
  getter: () => GetCmmdRuntimeInventoryParams,
): void {
  _refreshScopeGetter = getter;
}

export const cmmdRuntimeInventoryResource = createCmmdRuntimeInventoryResource({
  getRefreshScope: () => _refreshScopeGetter?.() ?? { allWorkspaces: true },
});

/**
 * The canonical reactive inventory reader. It owns passive, idempotent startup
 * so a direct thread route cannot depend on the sidebar or new-thread surface
 * mounting first.
 */
export function useCmmdRuntimeInventorySnapshot(): CmmdRuntimeInventoryResourceSnapshot {
  useEffect(() => {
    cmmdRuntimeInventoryResource.initialize();
  }, []);
  return useSyncExternalStore(
    cmmdRuntimeInventoryResource.subscribe,
    cmmdRuntimeInventoryResource.read,
    cmmdRuntimeInventoryResource.read,
  );
}
